/**
 * Confirmation dialog for leaving a level in progress. `SHELL-017`, `SHELL-019`.
 *
 * Not `window.confirm()`. A native dialog cannot be styled to match an
 * installed PWA, and it is invisible to axe: there is no DOM inside a
 * UA-native modal for a rule to inspect, so the one existing accessibility
 * gate this project has (ADR-0004) would pass a screen it cannot see into.
 *
 * `role="alertdialog"`, not `role="dialog"` -- this interrupts whatever the
 * player was doing to demand one of two answers before anything else can
 * happen, which is what the "alert" half of the role is for.
 *
 * The two-button focus trap here is deliberately hand-rolled rather than
 * reaching for a library: there are exactly two focusable elements, and
 * "the other one" is the whole of what wrapping Tab between them means.
 * `A11Y-021` requires the trap; it does not require it to be generic.
 */

import { useLayoutEffect, useRef } from 'preact/hooks';

import { focusScreenHeading } from '@shell/screen-transition';

export interface LeaveConfirmDialogProps {
  onStay: () => void;
  onLeave: () => void;
}

export function LeaveConfirmDialog({ onStay, onLeave }: LeaveConfirmDialogProps) {
  const stayRef = useRef<HTMLButtonElement | null>(null);
  const leaveRef = useRef<HTMLButtonElement | null>(null);

  // useLayoutEffect, not useEffect: focus moves synchronously with the DOM
  // mutation that shows or removes the dialog, the same choice Screen.tsx
  // makes for the document title, so neither move is a frame behind what
  // the player sees.
  useLayoutEffect(() => {
    // SHELL-019: focus moves into the dialog as it opens. `Stay` gets it, not
    // `Leave` -- the choice that discards nothing is what pressing Enter
    // immediately after the dialog appears should do.
    stayRef.current?.focus();

    // SHELL-019's other half. This fires when the dialog is removed for any
    // reason -- Stay, Leave, or Escape all end in the same unmount -- and
    // targets the same id every screen's own heading carries (Screen.tsx),
    // so it lands correctly whether the answer kept this screen or replaced
    // it with the next one.
    return () => {
      focusScreenHeading();
    };
  }, []);

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onStay();
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      const next = document.activeElement === stayRef.current ? leaveRef.current : stayRef.current;
      next?.focus();
    }
  };

  return (
    <div class="dialog-overlay">
      <div
        class="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="leave-confirm-heading"
        aria-describedby="leave-confirm-detail"
        onKeyDown={onKeyDown}
      >
        <h2 id="leave-confirm-heading">Leave this level?</h2>
        <p id="leave-confirm-detail">Your progress will be saved so you can resume it later.</p>
        <div class="dialog__actions">
          <button ref={stayRef} type="button" onClick={onStay}>
            Stay
          </button>
          <button ref={leaveRef} type="button" onClick={onLeave}>
            Leave
          </button>
        </div>
      </div>
    </div>
  );
}
